import { Button } from "@/components/button";
import { useProModal } from "@/components/pro-alert-box";
import Sidebar from "@/components/sidebar";
import { Table } from "@/components/table";
import { api } from "@/config/api-fetch";
import { KbType, useKnowledgeBase, useKnowledgeBaseStats } from "@/hooks/use-knowledge-base";
import { useModal } from "@/hooks/use-modal";
import { usePosts } from "@/hooks/use-posts";
import { useRoute } from "@/hooks/use-route";
import { OPTIONS, POST_TYPES } from "@/utils/constants";
import { css } from "@/utils/css";
import { getPaginatedList } from "@/utils/paginate";
import { useMemo, useState, useCallback } from "@wordpress/element";
import { __ } from "@wordpress/i18n";
import { format } from "date-fns";
import { toast } from "react-toastify";

const statuses = [
    {
        value: "all",
        label: __("All", "antimanual"),
    },
    {
        value: "added",
        label: __("Added", "antimanual"),
    },
    {
        value: "not_added",
        label: __("Not Added", "antimanual"),
    },
    {
        value: "requires_update",
        label: __("Requires Update", "antimanual"),
    },
];

export function WordPressPosts() {
    const { query, updateQuery } = useRoute();

    const selectedPostType = useMemo(() => {
        const type = POST_TYPES.find((type) => type.id === query.atml_post_type);
        return type || POST_TYPES[0];
    }, [query.atml_post_type]);

    const selectedStatus = useMemo(() => {
        const status = statuses.find((status) => status.value === query.kb_status);
        return (status || statuses[0]).value;
    }, [query.kb_status]);

    const page = useMemo(() => {
        return query.page_no ? parseInt(query.page_no, 10) : 1;
    }, [query.page_no]);

    const { data: stats, refetch: refetchStats } = useKnowledgeBaseStats();
    const { data: kbPosts, refetch, isLoading: kbPostsLoading } = useKnowledgeBase(KbType.wp, selectedPostType.id, -1);
    const { data, isLoading } = usePosts(selectedPostType.id, -1);
    const [postsOnAdd, setPostsOnAdd] = useState<string[]>([]);
    const [postsOnUpdate, setPostsOnUpdate] = useState<string[]>([]);
    const [postsOnDelete, setPostsOnDelete] = useState<string[]>([]);

    const { posts } = useMemo(() => {
        const posts: { [key: string]: any[] } = {
            all: [],
            added: [],
            not_added: [],
            requires_update: [],
        };

        data?.posts?.forEach((originalPost) => {
            const kbPost = kbPosts?.find((kb) => kb.post_id === originalPost.ID);

            const post = {
                ...originalPost,
                isAdded: false,
                requiresUpdate: false,
            };

            posts.all.push(post);

            if (kbPost) {
                post.isAdded = true;
                posts.added.push(post);

                if (new Date(kbPost.modified).getTime() < new Date(originalPost.post_modified_gmt).getTime()) {
                    post.requiresUpdate = true;
                    posts.requires_update.push(post);
                }
            } else {
                posts.not_added.push(post);
            }
        });

        return { posts };
    }, [data, kbPosts]);

    const { confirm } = useModal();
    const { showProAlert } = useProModal();

    const checkAndHandlePro = useCallback(() => {
        const total = stats?.find((stat) => stat.type === "wp")?.total || 0;
        const limit = 100;

        if (total >= limit) {
            showProAlert(
                __("Limit Exceeded", "antimanual") + ` (${total}/${limit})`,
                __(
                    "You have reached the limit of adding posts to the Knowledge Base. Upgrade to Pro to add more posts in the Knowledge Base.",
                    "antimanual"
                )
            );
            return true;
        }

        return false;
    }, [stats]);

    type Param = {
        knowledge_id: string;
        post_id: string;
    };

    const handleKbDelete = useCallback(
        async (ids: Param[]) => {
            if (!ids.length) return;

            const confirmed = await confirm(__("Are you sure you want to delete the item(s) from the Knowledge Base?", "antimanual"));
            if (!confirmed) return;

            await Promise.all(
                ids.map(async ({ knowledge_id, post_id }) => {
                    setPostsOnDelete((prev) => [...prev, post_id]);

                    try {
                        await api.delete("/knowledge-base?knowledge_id=" + knowledge_id);
                        await refetch().catch(() => {});
                        toast.success(__("Successfully removed from Knowledge Base", "antimanual"));
                    } catch (e: any) {
                        toast.error(e?.message || __("Failed to remove from Knowledge Base", "antimanual"));
                    }

                    setPostsOnDelete((prev) => prev.filter((pid) => pid !== post_id));
                })
            );
        },
        [confirm, refetch]
    );

    const handleKbUpdate = useCallback(
        async (ids: Param[]) => {
            if (!ids.length) return;

            await Promise.all(
                ids.map(async ({ knowledge_id, post_id }) => {
                    setPostsOnUpdate((prev) => [...prev, post_id]);

                    try {
                        await api.delete("/knowledge-base?knowledge_id=" + knowledge_id);
                        await api.post("/knowledge-base/wp", { post_id });
                        await refetch().catch(() => {});

                        toast.success(__("Successfully updated Knowledge Base", "antimanual"));
                    } catch (e: any) {
                        toast.error(e?.message || __("Failed to update Knowledge Base", "antimanual"));
                    }

                    setPostsOnUpdate((prev) => prev.filter((pid) => pid !== post_id));
                })
            );
        },
        [refetch]
    );

    const handleKbAdd = useCallback(
        async (ids: Param[]) => {
            if (checkAndHandlePro()) return;
            if (!ids.length) return;

            await Promise.all(
                ids.map(async ({ post_id }) => {
                    setPostsOnAdd((prev) => [...prev, post_id]);

                    try {
                        await api.post("/knowledge-base/wp", { post_id });
                        await refetch().catch(() => {});
                        toast.success(__("Successfully added to Knowledge Base", "antimanual"));
                    } catch (e: any) {
                        toast.error(e?.message || __("Failed to add to Knowledge Base", "antimanual"));
                    }

                    setPostsOnAdd((prev) => prev.filter((pid) => pid !== post_id));
                })
            );
        },
        [refetch, checkAndHandlePro]
    );

    const resolveParamArray = useCallback(
        (post_ids: string[]) => {
            return post_ids.map((post_id) => {
                const kbPost = kbPosts?.find((kb) => kb.post_id === Number(post_id));
                return { post_id: post_id, knowledge_id: kbPost?.id || "" };
            });
        },
        [kbPosts]
    );

    const actions = useMemo(() => {
        const [add, update, remove] = [
            {
                value: "add-to-kb",
                label: __("Add to Knowledge Base", "antimanual"),
                handler: (post_ids: string[]) => {
                    handleKbAdd(resolveParamArray(post_ids));
                },
            },
            {
                value: "update-kb",
                label: __("Update Knowledge Base", "antimanual"),
                handler: (post_ids: string[]) => {
                    handleKbUpdate(resolveParamArray(post_ids));
                },
            },
            {
                value: "remove-from-kb",
                label: __("Remove from Knowledge Base", "antimanual"),
                handler: (post_ids: string[]) => {
                    handleKbDelete(resolveParamArray(post_ids));
                },
            },
        ];

        if (selectedStatus === statuses[1].value) {
            return [update, remove];
        }

        if (selectedStatus === statuses[2].value) {
            return [add];
        }

        if (selectedStatus === statuses[3].value) {
            return [update, remove];
        }

        return [add, update, remove];
    }, [selectedStatus, handleKbAdd, handleKbUpdate, handleKbDelete, resolveParamArray]);

    const selectedAction = useMemo(() => {
        const action = actions.find((action) => action.value === query.kb_action);
        return (action || actions[0]).value;
    }, [query.kb_action, actions]);

    const columns = [
        {
            id: "title",
            label: __("Title", "antimanual"),
        },
        {
            id: "last_modified",
            label: __("Last Modified", "antimanual"),
        },
        {
            id: "added_in",
            label: __("Added In", "antimanual"),
        },
        {
            id: "status",
            label: __("Status", "antimanual"),
        },
        {
            id: "action",
            label: __("Action", "antimanual"),
        },
    ];

    const rows = useMemo(() => {
        const postsInCurrentPage = getPaginatedList(posts[selectedStatus] || [], page, OPTIONS.PAGINATION_LIMIT);

        return (
            postsInCurrentPage?.map((post) => {
                const kbPost = kbPosts?.find((kb) => kb.post_id === post.ID);
                const lastModified = post.post_modified_gmt ? new Date(post.post_modified_gmt) : null;
                const addedIn = kbPost?.modified ? new Date(kbPost.modified) : null;
                const status = post.isAdded ? (post.requiresUpdate ? statuses[3] : statuses[1]) : statuses[2];

                const post_id = post.ID.toString();
                const knowledge_id = kbPost?.id || "";

                return {
                    id: post_id,
                    title: (
                        <div className={css("column")}>
                            {post.post_title ? (
                                <a href={post.permalink} target="_blank">
                                    {post.post_title || __("(No Title)", "antimanual")}
                                </a>
                            ) : (
                                __("(No Title)", "antimanual")
                            )}

                            {(post.permalink || post.edit_link) && (
                                <div className={css("actions")}>
                                    <a href={post.edit_link} target="_blank">
                                        {__("Edit", "antimanual")}
                                    </a>
                                    |
                                    <a href={post.permalink} target="_blank">
                                        {__("View", "antimanual")}
                                    </a>
                                </div>
                            )}
                        </div>
                    ),
                    added_in: addedIn ? (
                        <div>
                            <div>{format(addedIn, "PP")}</div>
                            <div>{format(addedIn, "pp")}</div>
                        </div>
                    ) : null,
                    last_modified: lastModified ? (
                        <div>
                            <div>{format(lastModified, "PP")}</div>
                            <div>{format(lastModified, "pp")}</div>
                        </div>
                    ) : null,
                    status: (
                        <div className={css("column-kb_status")}>
                            <span className={css("status", status.value)}>{status.label}</span>
                        </div>
                    ),
                    action: post.isAdded ? (
                        <div className={css("flex item-center gap-1")}>
                            <Button
                                variant="secondary"
                                small
                                disabled={!post.requiresUpdate}
                                loading={postsOnUpdate.includes(post_id)}
                                onClick={() => handleKbUpdate([{ post_id, knowledge_id }])}
                            >
                                {__("Update", "antimanual")}
                            </Button>
                            <Button
                                variant="danger"
                                small
                                loading={postsOnDelete.includes(post_id)}
                                onClick={() => handleKbDelete([{ post_id, knowledge_id }])}
                            >
                                {__("Remove", "antimanual")}
                            </Button>
                        </div>
                    ) : (
                        <Button small loading={postsOnAdd.includes(post_id)} onClick={() => handleKbAdd([{ post_id, knowledge_id }])}>
                            {__("Add to Knowledge Base", "antimanual")}
                        </Button>
                    ),
                };
            }) || []
        );
    }, [posts, kbPosts, selectedStatus, page, postsOnAdd, postsOnUpdate, postsOnDelete, handleKbAdd, handleKbUpdate, handleKbDelete]);

    return (
        <div className={css("kb-option wp-posts")}>
            <Sidebar
                items={POST_TYPES}
                selectedItem={selectedPostType}
                onSelectItem={(item) => {
                    updateQuery({ atml_post_type: item.id, kb_status: statuses[0].value, page_no: "1" });
                }}
                className="my-0 border-none"
            >
                <h3 className={css("mt-0")}>{selectedPostType.label}</h3>
                <ul className={css("filters")}>
                    {statuses.map((status) => {
                        return (
                            <li key={status.value}>
                                <a
                                    href=""
                                    className={css(status.value, selectedStatus === status.value && "active")}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        updateQuery({ kb_status: status.value, page_no: "1" });
                                    }}
                                >
                                    {status.label}
                                    <span className={css("count")}>({posts[status.value]?.length || 0})</span>
                                </a>
                            </li>
                        );
                    })}
                </ul>
                <Table
                    key={`${selectedPostType.id}-${selectedStatus}`}
                    rows={rows}
                    columns={columns}
                    actions={actions}
                    rowsLoading={isLoading || kbPostsLoading}
                    selectedAction={selectedAction}
                    onActionSelect={(action) => updateQuery({ kb_action: action })}
                    pagination={{
                        total: posts?.[selectedStatus]?.length || 0,
                        limit: OPTIONS.PAGINATION_LIMIT,
                        currentPage: page,
                        onPageChange: (page) => updateQuery({ page_no: `${page}` }),
                    }}
                />
            </Sidebar>
        </div>
    );
}
